/**
 * Knit-top silhouettes: knit_tee, polo, henley, tank_top, crop_top,
 * sweatshirt, rugby_shirt, active_tee.
 */
import { OUTLINE_ATTRS, DETAIL_ATTRS, DETAIL_FILLED_ATTRS, STITCH_ATTRS } from "./_attrs.js";
import { partGroup } from "./_part-attrs.js";
import { ribBand, neckLabel } from "./_primitives.js";

import type { FlatView } from "../flat-svg.js";

/* ── Knit tee (short sleeve, rib neck) ─────────────────────────────── */
export function knit_teeFlat(view: FlatView): string {
  const neckDip = view === "front" ? 116 : 96;
  return `<g>
  ${partGroup("body", `<path ${OUTLINE_ATTRS} d="M 156 86 L 104 108 L 56 194 L 74 214 L 128 186 L 128 430 Q 200 440 272 430 L 272 186 L 326 214 L 344 194 L 296 108 L 244 86 Q 200 ${neckDip - 22} 156 86 Z"/>
  ${view === "front"
    ? `<rect x="186" y="94" width="28" height="13" rx="1.5" ${DETAIL_ATTRS}/>`
    : `<path ${STITCH_ATTRS} d="M 136 136 L 264 136"/>
  ${neckLabel(200, 86)}`}`)}
  ${partGroup("collar", `<path ${DETAIL_ATTRS} d="M 156 86 Q 200 ${neckDip} 244 86"/>
  <path ${STITCH_ATTRS} d="M 159 91 Q 200 ${neckDip + 6} 241 91"/>`)}
  ${partGroup("sleeve_left", `<path ${DETAIL_ATTRS} d="M 56 194 L 74 214"/>
  <path ${STITCH_ATTRS} d="M 63 190 L 80 208"/>
  <path ${STITCH_ATTRS} d="M 104 110 Q 118 148 128 186"/>
  <path ${STITCH_ATTRS} d="M 60 192 L 124 184"/>
  <path ${STITCH_ATTRS} d="M 58 188 L 126 180"/>`)}
  ${partGroup("sleeve_right", `<path ${DETAIL_ATTRS} d="M 344 194 L 326 214"/>
  <path ${STITCH_ATTRS} d="M 337 190 L 320 208"/>
  <path ${STITCH_ATTRS} d="M 296 110 Q 282 148 272 186"/>
  <path ${STITCH_ATTRS} d="M 340 192 L 276 184"/>
  <path ${STITCH_ATTRS} d="M 342 188 L 274 180"/>`)}
  ${partGroup("hem", `<path ${STITCH_ATTRS} d="M 129 420 Q 200 430 271 420"/>
  <path ${STITCH_ATTRS} d="M 128 426 Q 200 436 272 426"/>`)}
</g>`;
}

/* ── Polo (set-in sleeves, fold-over collar, button placket) ────────── */
export function poloFlat(view: FlatView): string {
  const neckDip = view === "front" ? 112 : 100;
  return `<g>
  ${partGroup("body", `<path ${OUTLINE_ATTRS} d="M 148 92 L 66 134 L 72 186 L 126 170 L 128 384 Q 200 396 272 384 L 274 170 L 328 186 L 334 134 L 252 92 Q 200 ${neckDip} 148 92 Z"/>
  <path ${DETAIL_ATTRS} d="M 128 368 L 128 384"/>
  <path ${DETAIL_ATTRS} d="M 272 368 L 272 384"/>
  ${view === "back" ? `${neckLabel(200, 88)}
  <path ${STITCH_ATTRS} d="M 130 130 L 270 130"/>` : ""}`)}
  ${partGroup("collar", view === "front"
    ? `<path ${DETAIL_FILLED_ATTRS} d="M 154 93 Q 200 ${neckDip - 8} 246 93 L 244 84 Q 200 ${neckDip - 18} 156 84 Z"/>
  <path ${STITCH_ATTRS} d="M 158 88 Q 200 ${neckDip - 14} 242 88"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 156 84 L 128 108 L 192 100 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 244 84 L 272 108 L 208 100 Z"/>
  <path ${STITCH_ATTRS} d="M 160 88 L 136 106 L 188 100"/>
  <path ${STITCH_ATTRS} d="M 240 88 L 264 106 L 212 100"/>`
    : `<path ${DETAIL_FILLED_ATTRS} d="M 154 93 Q 200 ${neckDip - 4} 246 93 L 244 84 Q 200 ${neckDip - 10} 156 84 Z"/>
  <path ${STITCH_ATTRS} d="M 158 88 Q 200 ${neckDip - 8} 242 88"/>`)}
  ${view === "front" ? partGroup("placket", `<path ${DETAIL_ATTRS} d="M 194 100 L 194 194 L 206 194 L 206 100"/>
  <path ${STITCH_ATTRS} d="M 198 104 L 198 190"/>
  <circle cx="200" cy="122" r="3.5" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="200" cy="148" r="3.5" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="200" cy="174" r="3.5" ${DETAIL_FILLED_ATTRS}/>`) : ""}
  ${partGroup("sleeve_left", `<path ${DETAIL_ATTRS} d="M 78 140 L 82 178"/>
  <path ${STITCH_ATTRS} d="M 82 142 L 86 176"/>
  <path ${STITCH_ATTRS} d="M 148 94 Q 140 132 126 170"/>`)}
  ${partGroup("sleeve_right", `<path ${DETAIL_ATTRS} d="M 322 140 L 318 178"/>
  <path ${STITCH_ATTRS} d="M 318 142 L 314 176"/>
  <path ${STITCH_ATTRS} d="M 252 94 Q 260 132 274 170"/>`)}
  ${partGroup("hem", `<path ${STITCH_ATTRS} d="M 129 376 Q 200 388 271 376"/>
  <path ${STITCH_ATTRS} d="M 128 380 Q 200 392 272 380"/>`)}
</g>`;
}

/* ── Henley (tee + short button placket, no collar) ─────────────────── */
export function henleyFlat(view: FlatView): string {
  const neckDip = view === "front" ? 118 : 96;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 156 86 L 104 108 L 56 194 L 74 214 L 128 186 L 128 430 Q 200 440 272 430 L 272 186 L 326 214 L 344 194 L 296 108 L 244 86 Q 200 ${neckDip - 22} 156 86 Z"/>
  <path ${DETAIL_ATTRS} d="M 156 86 Q 200 ${neckDip} 244 86"/>
  <path ${STITCH_ATTRS} d="M 159 91 Q 200 ${neckDip + 6} 241 91"/>
  <path ${DETAIL_ATTRS} d="M 56 194 L 74 214"/>
  <path ${DETAIL_ATTRS} d="M 344 194 L 326 214"/>
  <path ${STITCH_ATTRS} d="M 63 190 L 80 208"/>
  <path ${STITCH_ATTRS} d="M 337 190 L 320 208"/>
  <path ${STITCH_ATTRS} d="M 104 110 Q 118 148 128 186"/>
  <path ${STITCH_ATTRS} d="M 296 110 Q 282 148 272 186"/>
  <path ${STITCH_ATTRS} d="M 129 420 Q 200 430 271 420"/>
  <path ${STITCH_ATTRS} d="M 128 426 Q 200 436 272 426"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 193 96 L 193 178 L 207 178 L 207 96"/>
  <path ${STITCH_ATTRS} d="M 197 100 L 197 174"/>
  <circle cx="200" cy="118" r="3.5" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="200" cy="140" r="3.5" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="200" cy="162" r="3.5" ${DETAIL_FILLED_ATTRS}/>`
      : `<path ${STITCH_ATTRS} d="M 136 136 L 264 136"/>
  ${neckLabel(200, 86)}`
  }
</g>`;
}

/* ── Tank top / vest (narrow straps, deep scooped armholes, straight body) ── */
export function tank_topFlat(view: FlatView): string {
  const neckDip = view === "front" ? 138 : 104;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 172 86 L 140 78 Q 110 112 104 174 Q 100 224 126 262 L 132 430 Q 200 442 268 430 L 274 262 Q 300 224 296 174 Q 290 112 260 78 L 228 86 Q 200 ${neckDip} 172 86 Z"/>
  <path ${DETAIL_ATTRS} d="M 172 86 Q 200 ${neckDip} 228 86"/>
  <path ${STITCH_ATTRS} d="M 175 90 Q 200 ${neckDip + 5} 225 90"/>
  <path ${STITCH_ATTRS} d="M 108 142 Q 104 198 124 252"/>
  <path ${STITCH_ATTRS} d="M 292 142 Q 296 198 276 252"/>
  <path ${STITCH_ATTRS} d="M 133 420 Q 200 432 267 420"/>
  <path ${STITCH_ATTRS} d="M 132 426 Q 200 438 268 426"/>
  ${
    view === "front"
      ? `<rect x="186" y="92" width="28" height="12" rx="1.5" ${DETAIL_ATTRS}/>
  <path ${STITCH_ATTRS} d="M 150 262 L 150 420"/>
  <path ${STITCH_ATTRS} d="M 250 262 L 250 420"/>`
      : `<path ${STITCH_ATTRS} d="M 148 124 L 252 124"/>
  ${neckLabel(200, 78)}
  <path ${STITCH_ATTRS} d="M 152 262 L 152 420"/>
  <path ${STITCH_ATTRS} d="M 248 262 L 248 420"/>`
  }
</g>`;
}

/* ── Crop top (short body, thin straps or cap sleeves) ──────────────── */
export function crop_topFlat(view: FlatView): string {
  const neckDip = view === "front" ? 126 : 100;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 166 82 Q 200 ${neckDip - 22} 234 82 L 268 90 Q 282 102 276 126 Q 270 138 262 136 L 262 310 Q 200 322 138 310 L 138 136 Q 130 138 124 126 Q 118 102 132 90 Z"/>
  <path ${DETAIL_ATTRS} d="M 166 82 Q 200 ${neckDip} 234 82"/>
  <path ${STITCH_ATTRS} d="M 169 87 Q 200 ${neckDip + 6} 231 87"/>
  <path ${STITCH_ATTRS} d="M 120 122 Q 132 134 142 132"/>
  <path ${STITCH_ATTRS} d="M 280 122 Q 268 134 258 132"/>
  <path ${STITCH_ATTRS} d="M 139 300 Q 200 312 261 300"/>
  <path ${STITCH_ATTRS} d="M 138 306 Q 200 318 262 306"/>
  ${
    view === "front"
      ? `<rect x="186" y="90" width="28" height="12" rx="1.5" ${DETAIL_ATTRS}/>`
      : `<path ${STITCH_ATTRS} d="M 146 118 L 254 118"/>
  ${neckLabel(200, 82)}`
  }
</g>`;
}

/* ── Sweatshirt (crew neck, long sleeves, rib bands) ──────────────── */
export function sweatshirtFlat(view: FlatView): string {
  const neckDip = view === "front" ? 110 : 96;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 158 90 L 100 108 L 56 238 L 38 344 L 96 358 L 118 270 L 132 186 L 130 418 L 270 418 L 268 186 L 282 270 L 304 358 L 362 344 L 344 238 L 300 108 L 242 90 Q 200 ${neckDip - 22} 158 90 Z"/>
  <path ${DETAIL_ATTRS} d="M 158 90 Q 200 ${neckDip} 242 90"/>
  <path ${STITCH_ATTRS} d="M 161 95 Q 200 ${neckDip + 6} 239 95"/>
  ${ribBand(418, 130, 270, 14)}
  <path ${DETAIL_FILLED_ATTRS} d="M 38 344 L 96 358 L 90 380 L 32 366 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 362 344 L 304 358 L 310 380 L 368 366 Z"/>
  <path ${STITCH_ATTRS} d="M 100 110 Q 122 148 132 186"/>
  <path ${STITCH_ATTRS} d="M 300 110 Q 278 148 268 186"/>
  ${
    view === "front"
      ? `<rect x="186" y="96" width="28" height="12" rx="1.5" ${DETAIL_ATTRS}/>`
      : `<path ${STITCH_ATTRS} d="M 140 132 L 260 132"/>
  ${neckLabel(200, 90)}`
  }
</g>`;
}

/* ── Rugby shirt (polo-style collar, long sleeves, horizontal stripes) ── */
export function rugby_shirtFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 152 86 L 100 106 L 56 236 L 38 340 L 96 354 L 116 268 L 128 184 L 128 426 Q 200 436 272 426 L 272 184 L 284 268 L 304 354 L 362 340 L 344 236 L 300 106 L 248 86 Q 200 76 152 86 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 38 340 L 96 354 L 90 376 L 32 362 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 362 340 L 304 354 L 310 376 L 368 362 Z"/>
  <path ${STITCH_ATTRS} d="M 100 108 Q 118 146 128 184"/>
  <path ${STITCH_ATTRS} d="M 300 108 Q 282 146 272 184"/>
  <path ${DETAIL_ATTRS} d="M 130 230 L 270 230"/>
  <path ${DETAIL_ATTRS} d="M 130 280 L 270 280"/>
  <path ${DETAIL_ATTRS} d="M 130 330 L 270 330"/>
  <path ${STITCH_ATTRS} d="M 129 416 Q 200 426 271 416"/>
  <path ${STITCH_ATTRS} d="M 128 422 Q 200 432 272 422"/>
  ${
    view === "front"
      ? `<path ${DETAIL_FILLED_ATTRS} d="M 156 84 C 172 74 228 74 244 84 L 254 100 L 208 122 L 200 116 L 192 122 L 146 100 Z"/>
  <path ${DETAIL_ATTRS} d="M 193 120 L 193 186 L 207 186 L 207 120"/>
  <path ${STITCH_ATTRS} d="M 197 124 L 197 182"/>
  <circle cx="202" cy="148" r="4" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="202" cy="172" r="4" ${DETAIL_FILLED_ATTRS}/>`
      : `<path ${DETAIL_FILLED_ATTRS} d="M 156 84 C 172 74 228 74 244 84 L 248 96 L 152 96 Z"/>
  <path ${STITCH_ATTRS} d="M 132 126 L 268 126"/>`
  }
</g>`;
}

/* ── Active tee (raglan sleeves, slimmer fit) ──────────────────────── */
export function active_teeFlat(view: FlatView): string {
  const neckDip = view === "front" ? 114 : 96;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 160 86 L 108 106 L 62 190 L 78 210 L 130 184 L 130 424 Q 200 434 270 424 L 270 184 L 322 210 L 338 190 L 292 106 L 240 86 Q 200 ${neckDip - 22} 160 86 Z"/>
  <path ${DETAIL_ATTRS} d="M 160 86 Q 200 ${neckDip} 240 86"/>
  <path ${STITCH_ATTRS} d="M 163 91 Q 200 ${neckDip + 6} 237 91"/>
  <path ${DETAIL_ATTRS} d="M 62 190 L 78 210"/>
  <path ${DETAIL_ATTRS} d="M 338 190 L 322 210"/>
  <path ${STITCH_ATTRS} d="M 69 186 L 84 204"/>
  <path ${STITCH_ATTRS} d="M 331 186 L 316 204"/>
  <path ${DETAIL_ATTRS} d="M 160 86 L 130 184"/>
  <path ${DETAIL_ATTRS} d="M 240 86 L 270 184"/>
  <path ${STITCH_ATTRS} d="M 162 90 L 134 180"/>
  <path ${STITCH_ATTRS} d="M 238 90 L 266 180"/>
  <path ${STITCH_ATTRS} d="M 131 414 Q 200 424 269 414"/>
  <path ${STITCH_ATTRS} d="M 130 420 Q 200 430 270 420"/>
  ${
    view === "front"
      ? `<rect x="186" y="94" width="28" height="12" rx="1.5" ${DETAIL_ATTRS}/>`
      : `<path ${STITCH_ATTRS} d="M 138 132 L 262 132"/>
  ${neckLabel(200, 86)}`
  }
</g>`;
}
